import React from 'react';
import ModalDlg from 'components/Basic/ModalDlg';
import { Close } from '@material-ui/icons';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DetailField = {
  label: React.ReactNode;
  value: React.ReactNode;
  fullWidth?: boolean;
};

export type DetailSection = {
  icon: React.ReactElement;
  title: React.ReactNode;
  fields?: DetailField[];
  /** Render custom content instead of the default field grid */
  children?: React.ReactNode;
};

type DetailModalProps = {
  /** CSS class applied to the ModalDlg wrapper (for per-modal SCSS scoping) */
  className: string;
  visible: boolean;
  width?: number;
  /** First line in the dark header */
  title: React.ReactNode;
  /** Second line in the dark header — code badge, subtitle, etc. */
  subtitle?: React.ReactNode;
  /** Single letter / initials shown in the yellow avatar square */
  avatarLetter?: string;
  onClose: () => void;
  sections: DetailSection[];
};

// ── Component ─────────────────────────────────────────────────────────────────

const DetailModal: React.FC<DetailModalProps> = ({
  className,
  visible,
  width = 560,
  title,
  subtitle,
  avatarLetter,
  onClose,
  sections,
}) => {
  return (
    <ModalDlg className={`detail_modal_wrapper ${className}`} visible={visible} width={width} handleCancel={onClose} isCenter>
      <div className="detail_modal_content">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="detail_modal_header">
          <div className="detail_modal_header_left">
            {avatarLetter && (
              <div className="detail_modal_avatar">
                {avatarLetter}
              </div>
            )}
            <div className="detail_modal_header_info">
              <h2 className="detail_modal_title">{title}</h2>
              {subtitle && (
                <span className="detail_modal_subtitle">{subtitle}</span>
              )}
            </div>
          </div>
          <button className="detail_modal_close" onClick={onClose}>
            <Close fontSize="small" />
          </button>
        </div>

        {/* ── Sections ────────────────────────────────────────────────── */}
        {sections.map((section, i) => (
          <div className="detail_modal_section" key={i}>
            <p className="detail_modal_section_title">
              <span className="detail_modal_section_icon">
                {React.cloneElement(section.icon, { fontSize: 'inherit' } as any)}
              </span>
              {section.title}
            </p>

            {section.children ? (
              section.children
            ) : (
              <div className="detail_modal_grid">
                {(section.fields || []).map((field, j) => (
                  <div
                    className={`detail_modal_item${field.fullWidth ? ' full_width' : ''}`}
                    key={j}
                  >
                    <span className="detail_modal_label">{field.label}</span>
                    <span className="detail_modal_value">{field.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

      </div>
    </ModalDlg>
  );
};

export default DetailModal;
